import { useMemo } from 'react';
import { normalizeJobDetail, type JobDetailNormalizationResult } from '../../../../../utils/data/normalize-job-detail';
import { transformJobForLanguage } from '../utils/transformJob';
import type { DisplayJob } from '../../../../../types';
import type { FinalJob } from '../../../../../lambda/types';

export interface JobNormalizationResult {
  displayJob: DisplayJob | null;
  finalJob: FinalJob | null;
  normalizationMeta: JobDetailNormalizationResult | null;
}

export interface UseJobNormalizationProps {
  job: FinalJob | null | undefined;
  jobLocale: string;
  seoSlug?: string;
}

/**
 * Normalize job data for display (language transform + detail normalization).
 * Derived synchronously from `job` only — no useState/useEffect — so the first client paint
 * after SSR / island mount never briefly shows an empty tree while state catches up.
 */
export function useJobNormalization({
  job,
  jobLocale,
  seoSlug,
}: UseJobNormalizationProps): JobNormalizationResult {
  return useMemo(() => {
    if (!job) {
      return { displayJob: null, finalJob: null, normalizationMeta: null };
    }

    const transformedJob = transformJobForLanguage(job, jobLocale) as DisplayJob;
    const normalizationResult = normalizeJobDetail(job, transformedJob, {
      locale: jobLocale,
      seoSlug,
    });

    return {
      displayJob: normalizationResult.displayJob,
      finalJob: normalizationResult.canonicalJob,
      normalizationMeta: normalizationResult,
    };
  }, [job, jobLocale, seoSlug]);
}
